/**
 * The community review writer's handoff URL (contract-review-photos §7):
 *
 *   /community/review/new?product=<slug>&buy_again=yes|no&src=<key>&from=<same-origin path>
 *
 * `src` names the ask that sent the reader (the analytics `location` of the writer's
 * `band_opened`, and the reward screen's way back label); `from` is where ×, Close, Done and
 * "Back …" return. Both are reader input: validated here, before anything renders them, and the
 * page treats a value that fails as absent. Pure, no React; tests `community-review.spec.ts`.
 *
 * The session keys for the writer's draft and for "a review was just published" live here too —
 * one spelling for the writer that sets them and `own-note.ts` that reads them.
 */
import { publicConfig } from 'config'
import type { OwnerShelfItem } from 'lib/dealhunt/types'
import { productSlug, withSlash } from 'lib/dealhunt/url'
import { communityHref, newReviewHref, productSlugParam, threadHref } from './model'
import type { OwnerNoteSeed } from './review'
import { SIGN_IN_NEEDED } from 'lib/owner-notes/band-copy'

/** Every ask that can hand off to the writer, as `src` — each the `location` its page's
 *  `*_owner_ask_shown` reports, so the writer's opens line up with that ask's impressions. A
 *  guide's asks: `strip`, `roster`, `seated`, `rail` (`guide` = a guide ask not named otherwise).
 *  `reminder` is DealHunt's declared-not-reviewed bell deep link (`&src=reminder`, no `from`) —
 *  it has no `*_ask_shown` counterpart. `home` went with the home band (187ba5c7, 2026-10-09):
 *  an old `src=home` link is read as no src (`location: community`). */
export const WRITER_SOURCES = [
  'strip',
  'guide',
  'roster',
  'seated',
  'section',
  'rail',
  'hub',
  'post',
  'gear',
  'reminder',
] as const
export type WriterSource = (typeof WRITER_SOURCES)[number]

/** One value of a query param (a repeated `?src=a&src=b` arrives as an array: not served). */
function single(raw: unknown): string | null {
  return typeof raw === 'string' && raw ? raw : null
}

export function writerSourceParam(raw: unknown): WriterSource | null {
  const value = single(raw)
  return value && (WRITER_SOURCES as readonly string[]).includes(value)
    ? (value as WriterSource)
    : null
}

/** The contract's `from` shape: a lowercase same-origin path, an optional `#anchor`. */
const FROM_PATTERN = /^\/[a-z0-9\-/]*(#[a-z0-9-]+)?$/
const FROM_MAX = 200

/**
 * `?from=` as a same-origin relative path, or null. `//host` matches the contract's character
 * class but a browser reads it as another site (protocol-relative), so it is refused here.
 */
export function writerFromParam(raw: unknown): string | null {
  const value = single(raw)
  // `//host` is another site; `/api/…` is never a page to go back to.
  if (!value || value.length > FROM_MAX || value.startsWith('//') || value.startsWith('/api/')) {
    return null
  }
  return FROM_PATTERN.test(value) ? value : null
}

/** `?buy_again=yes|no` → the strip's tapped answer; anything else = not answered. */
export function buyAgainParam(raw: unknown): boolean | null {
  const value = single(raw)
  if (value === 'yes') {
    return true
  }
  return value === 'no' ? false : null
}

/** The writer's handoff params once validated — what the page passes the composer. */
export interface WriterHandoff {
  src: WriterSource | null
  from: string | null
  buyAgain: boolean | null
  /** `?tab=new`: the ask opened the writer in a new tab (desktop) — the origin tab stays open. */
  newTab: boolean
}

export function writerHandoffParams(query: Record<string, unknown>): WriterHandoff {
  return {
    src: writerSourceParam(query.src),
    from: writerFromParam(query.from),
    buyAgain: buyAgainParam(query.buy_again),
    newTab: single(query.tab) === 'new',
  }
}

/** The writer for one product, with whatever handoff params are known. */
export function writerHref(
  input: {
    product: string
    buyAgain?: boolean | null
    src?: WriterSource | null
    from?: string | null
    /** The ask opens the writer in a new tab: `&tab=new`. */
    newTab?: boolean
  },
  trailingSlash: boolean
): string {
  const params = [newReviewHref(input.product, trailingSlash)]
  if (typeof input.buyAgain === 'boolean') {
    params.push(`buy_again=${input.buyAgain ? 'yes' : 'no'}`)
  }
  if (input.src) {
    params.push(`src=${input.src}`)
  }
  const from = writerFromParam(input.from)
  if (from) {
    params.push(`from=${encodeURIComponent(from)}`)
  }
  if (input.newTab) {
    params.push('tab=new')
  }
  return params.join('&')
}

// ── the flag (P1) ─────────────────────────────────────────────────────────────

/**
 * `NEXT_PUBLIC_OWNER_WRITER_HANDOFF`: a comma list of `src` keys, or `all`. Unknown keys are
 * ignored; unset or empty = no surface hands off (every ask keeps its inline band).
 */
export function parseHandoffFlag(raw: string | null | undefined): ReadonlySet<WriterSource> {
  const keys = (raw ?? '')
    .split(',')
    .map(k => k.trim().toLowerCase())
    .filter(Boolean)
  if (keys.includes('all')) {
    return new Set(WRITER_SOURCES)
  }
  return new Set(keys.flatMap(k => writerSourceParam(k) ?? []))
}

/** Does this ask hand off to the writer? Same value on the server and in the browser
 *  (NEXT_PUBLIC), so the SSR and the hydrated ask agree. */
export function handoffEnabled(
  src: WriterSource,
  raw: string | null | undefined = publicConfig('ownerWriterHandoff')
): boolean {
  return parseHandoffFlag(raw).has(src)
}

/** The SoundGuys slug of a `/me` shelf row — My gear's and the Gear tab's edit link, "Your gear"
 *  in the picker. Here, not in `OwnPicker`, so My gear does not bundle the picker for it. */
export function shelfSlug(
  row: Pick<OwnerShelfItem, 'node_slug' | 'brand_name' | 'brand_slug'>
): string | null {
  return row.node_slug
    ? productSlugParam(productSlug(row.brand_name ?? null, row.node_slug, row.brand_slug ?? null))
    : null
}

/** The SoundGuys product slug of a product-page address (`/product/<slug>`, `dealHuntProductHref`),
 *  or null — a category path, another page, a missing href. */
export function productSlugFromHref(href: string | null | undefined): string | null {
  const match = /^\/product\/([a-z0-9-]{1,120})\/?$/.exec(href ?? '')
  return match ? match[1] : null
}

/**
 * The accepted review's own page in the community (its thread), from the product page address
 * the host was given — the same slug the reward screen's `product.slug` is (what follows
 * `/product/`). null without a product page (My gear) or with an address that is not one.
 */
export function communityThreadHref(
  productHref: string | null | undefined,
  noteId: string,
  trailingSlash: boolean
): string | null {
  const slug = productSlugFromHref(productHref)
  return slug ? threadHref(slug, noteId, trailingSlash) : null
}

/** The reward screen's way back, per ask (contract §7). */
export const WRITER_BACK_LABEL: Record<WriterSource, string> = {
  strip: 'Back to the category',
  guide: 'Back to the guide',
  roster: 'Back to the guide',
  seated: 'Back to the guide',
  section: 'Back to the product page',
  rail: 'Back to the product page',
  post: 'Back to the review',
  gear: 'Back to My gear',
  hub: 'Back to community',
  reminder: 'Back to community',
}

export const BACK_TO_COMMUNITY = 'Back to community'

/** My gear on `/profile` — the one way back that needs a query (`?tab=gear`), so it is a fixed
 *  address here rather than a `from` (the contract's `from` admits no query). */
export function myGearHref(trailingSlash: boolean): string {
  return `${withSlash('/profile', trailingSlash)}?tab=gear`
}

/**
 * Where ×, Close, Done and "Back …" lead, and what the reward screen calls it. The `src` label
 * only with a `from` to honour it (a label that says "the category" must lead there) — except
 * `gear`, whose way back is always My gear (a `from` is ignored); otherwise the community home.
 * `strip` and `rail` are asked on a guide too: away from `/product/…` they say "the guide".
 */
export function writerBack(
  handoff: Pick<WriterHandoff, 'src' | 'from'>,
  trailingSlash: boolean
): { href: string; label: string } {
  if (handoff.src === 'gear') {
    return { href: myGearHref(trailingSlash), label: WRITER_BACK_LABEL.gear }
  }
  if (handoff.from) {
    const onGuide =
      (handoff.src === 'strip' || handoff.src === 'rail') && !handoff.from.startsWith('/product/')
    let label = handoff.src ? WRITER_BACK_LABEL[handoff.src] : BACK_TO_COMMUNITY
    if (onGuide) {
      label = WRITER_BACK_LABEL.guide
    }
    return { href: handoff.from, label }
  }
  return { href: communityHref(trailingSlash), label: BACK_TO_COMMUNITY }
}

/**
 * The new-tab reward line (`?tab=new`): the origin tab is still open and re-reads itself
 * (`own-note.ts`, the localStorage mark), so the review is already in place there.
 */
export function newTabReturnLine(backLabel: string): string {
  const place = backLabel.replace(/^Back to (the )?/, '')
  return `Back on the ${place} tab, your review is in place.`
}

// ── the handoff hint and the new tab (desktop) ───────────────────────────────

/** Desktop: a fine pointer that hovers AND a viewport at least this wide opens a new tab. */
export const NEW_TAB_POINTER_QUERY = '(hover: hover) and (pointer: fine)'
export const NEW_TAB_MIN_WIDTH = 768

/** Does this browser open the writer in a new tab? Decided once, client-side, after mount — the
 *  server render (and the hydrating one) is always same-tab. Never throws. */
export function prefersNewTab(
  win: Pick<Window, 'innerWidth'> & { matchMedia?: Window['matchMedia'] }
): boolean {
  try {
    return (
      win.innerWidth >= NEW_TAB_MIN_WIDTH &&
      Boolean(win.matchMedia?.(NEW_TAB_POINTER_QUERY).matches)
    )
  } catch {
    return false
  }
}

/**
 * The one muted line under (or beside) a handed-off ask: the CTA leaves the page, so it says
 * what opens. No duration (the band never promises one). Gear edits an existing review.
 */
export function handoffHint(src: WriterSource, newTab: boolean): string {
  if (src === 'gear') {
    return 'Opens your review in the writer.'
  }
  return newTab
    ? 'Opens the review writer in a new tab: three questions, one sentence, photos if you like. This page stays as it is.'
    : 'Opens the review writer: three questions, one sentence, photos if you like. You come back here when you publish.'
}

/** The review post's handed-off ask: the link text carries its own hint. */
export function postAskLabel(productName: string): string {
  return `Own the ${productName}? Write an owner review — opens the review writer.`
}

/** `postAskLabel` under the sign-in gate (the writer is gated): the requirement, at the ask. */
export function gatedPostAskLabel(productName: string): string {
  return `${postAskLabel(productName)} ${SIGN_IN_NEEDED}`
}

/** "Edit owner review" once the reader arrives in edit mode; the server HTML says "New". */
export function writerCrumb(editing: boolean): string {
  return editing ? 'Edit owner review' : 'New owner review'
}

// ── session keys ──────────────────────────────────────────────────────────────

/** The writer's unsent draft for one product (`{answers, note}`), this tab only. */
export function reviewDraftKey(slug: string): string {
  return `sg:review-draft:v1:${slug}`
}

/** The writer's uploaded, not-yet-published review photos for one product (media ids, ≤ 23 h —
 *  `lib/owner-notes/review-photos`), this tab only. The band's own slot: the draft above (the
 *  band's `seed`) stays photo-less. */
export function reviewPhotosKey(slug: string): string {
  return `sg:review-photos:v1:${slug}`
}

/** Set by the writer when DealHunt answered a publish for this product: the page the reader
 *  returns to re-reads `/me` once (`own-note.ts`), then clears it. */
export const REVIEW_PUBLISHED_PREFIX = 'sg:review-published:'
export function reviewPublishedKey(slug: string): string {
  return `${REVIEW_PUBLISHED_PREFIX}${slug}`
}

/**
 * Take every "a review was just published" mark out of `storage`; true when there was one. The
 * page the reader returns to then re-reads `/me` once (`own-note.ts`). Never throws: blocked
 * storage means no mark.
 */
/** The same mark for another tab (the new-tab handoff): sessionStorage is per tab, so the
 *  writer writes it to localStorage too, and the origin tab hears the `storage` event. */
export function isPublishedMarkKey(key: string | null): boolean {
  return Boolean(key?.startsWith(REVIEW_PUBLISHED_PREFIX))
}

export function consumePublishedMarks(
  storage: Pick<Storage, 'length' | 'key' | 'removeItem'> | null
): boolean {
  if (!storage) {
    return false
  }
  try {
    const keys: string[] = []
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i)
      if (key?.startsWith(REVIEW_PUBLISHED_PREFIX)) {
        keys.push(key)
      }
    }
    keys.forEach(key => storage.removeItem(key))
    return keys.length > 0
  } catch {
    return false
  }
}

function finiteOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

/**
 * A stored draft as the band's `seed`, or null when it is absent, unreadable or empty. Only the
 * shapes the band holds survive; an option id DealHunt no longer offers is harmless (the chip
 * just shows unselected).
 */
export function parseStoredDraft(raw: string | null): OwnerNoteSeed | null {
  if (!raw) {
    return null
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== 'object') {
    return null
  }
  const { answers, note } = parsed as { answers?: unknown; note?: unknown }
  if (!answers || typeof answers !== 'object' || typeof note !== 'string') {
    return null
  }
  const a = answers as Record<string, unknown>
  const str = (v: unknown): string => (typeof v === 'string' ? v : '')
  const seed: OwnerNoteSeed = {
    answers: {
      ownership_length: str(a.ownership_length),
      still_using: str(a.still_using),
      use_cases: Array.isArray(a.use_cases)
        ? a.use_cases.filter((u): u is string => typeof u === 'string')
        : [],
      reads:
        a.reads && typeof a.reads === 'object'
          ? (Object.fromEntries(
              Object.entries(a.reads as Record<string, unknown>).filter(
                ([, v]) => typeof v === 'string'
              )
            ) as OwnerNoteSeed['answers']['reads'])
          : {},
      gave_out:
        typeof a.gave_out === 'string'
          ? (a.gave_out as OwnerNoteSeed['answers']['gave_out'])
          : null,
      hours_per_charge: finiteOrNull(a.hours_per_charge),
      buy_again: typeof a.buy_again === 'boolean' ? a.buy_again : null,
    },
    note,
  }
  const empty =
    !seed.note.trim() &&
    !seed.answers.ownership_length &&
    !seed.answers.still_using &&
    seed.answers.use_cases.length === 0 &&
    Object.keys(seed.answers.reads).length === 0 &&
    seed.answers.gave_out === null &&
    seed.answers.hours_per_charge === null &&
    seed.answers.buy_again === null
  return empty ? null : seed
}
